import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BRAND_COLOR_PATTERN, isEntitled, type OrgBranding } from '@emr/shared';

import { fetchOrgBranding, updateOrgBranding, updateOrgLogo } from '../api/orgBranding.ts';
import { ApiError } from '../api/client.ts';
import { useSession } from '../auth/sessionContext.ts';
import { AsyncSection } from '../components/AsyncState.tsx';
import { GatedRoute, UpgradeRefusal } from '../components/GatedRoute.tsx';
import { Button, ErrorText, Field, Panel } from '../components/primitives.tsx';
import { orgBrandingQueryKey } from '../theme/OrgBrandingSync.tsx';

/**
 * Practice branding settings (`BACKLOG.md` B4) — a practice's own colour scheme and, on an
 * entitled tier, its own logo. `org:manage_settings` gates the whole page (`GatedRoute`, matching
 * `server/src/orgs/brandingRoutes.ts`'s identical capability); the logo section additionally checks
 * `isEntitled(session.tier, 'whiteLabel')` itself, inline, rather than gating the whole page on it —
 * a lower tier can still set its colour scheme here, only the logo half is unavailable, so a
 * page-level `feature` gate (which would hide colour too) would be the wrong shape.
 */
export function PracticeBrandingPage() {
  const { t } = useTranslation('branding');
  return (
    <GatedRoute title={t('heading')} capability="org:manage_settings" feature={null}>
      <div className="flex flex-col gap-4">
        <p className="m-0 text-sm text-ink-muted">{t('intro')}</p>
        <ColorSection />
        <LogoSection />
      </div>
    </GatedRoute>
  );
}

function describeError(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.message;
  return fallback;
}

// --- Colour + the per-user override toggle ----------------------------------------------------

function ColorSection() {
  const { t } = useTranslation('branding');
  const session = useSession();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: orgBrandingQueryKey(session.orgId),
    queryFn: ({ signal }) => fetchOrgBranding(signal),
  });

  const [colorInput, setColorInput] = useState('');
  const [allowUserColorPicker, setAllowUserColorPicker] = useState(true);
  const [formatError, setFormatError] = useState<string | null>(null);
  // What the two fields above were last seeded from — `react-query`'s structural sharing keeps
  // `query.data` referentially stable across a refetch that returns identical content, so this
  // only re-seeds on a genuine change (the initial load, or another admin's concurrent save),
  // never mid-edit and never on every render. Adjusting state from a prop/query value during
  // render, guarded by a comparison like this, is the pattern React itself documents for this
  // exact "sync a form's local draft from a query result" shape — deliberately not a `useEffect`,
  // which would apply the seed one render late and trip `react-hooks/set-state-in-effect`.
  const [syncedFrom, setSyncedFrom] = useState<OrgBranding | undefined>(undefined);
  if (query.data !== undefined && query.data !== syncedFrom) {
    setSyncedFrom(query.data);
    setColorInput(query.data.brandColor ?? '');
    setAllowUserColorPicker(query.data.allowUserColorPicker);
  }

  const save = useMutation({
    mutationFn: (input: { brandColor: string | null; allowUserColorPicker: boolean }) =>
      updateOrgBranding(input),
    onSuccess: (branding: OrgBranding) => {
      queryClient.setQueryData(orgBrandingQueryKey(session.orgId), branding);
    },
  });

  const trimmed = colorInput.trim();
  const brandColor = trimmed === '' ? null : trimmed;
  const colorIsValid = brandColor === null || BRAND_COLOR_PATTERN.test(brandColor);

  return (
    <Panel>
      <AsyncSection query={query} loadingLabel={t('loading')}>
        {() => (
          <form
            className="flex flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              setFormatError(null);
              if (!colorIsValid) {
                setFormatError(t('color.formatError'));
                return;
              }
              save.mutate({ brandColor, allowUserColorPicker });
            }}
          >
            <div className="flex flex-wrap items-end gap-3">
              <Field
                label={t('color.label')}
                hint={t('color.hint')}
                placeholder="#1c716b"
                value={colorInput}
                {...(formatError !== null ? { error: formatError } : {})}
                onChange={(event) => {
                  setColorInput(event.target.value);
                }}
                className="max-w-40 font-mono"
              />
              <input
                type="color"
                aria-label={t('color.pickerAriaLabel')}
                value={colorIsValid && brandColor !== null ? brandColor : '#1c716b'}
                onChange={(event) => {
                  setColorInput(event.target.value);
                }}
                className="h-10 w-14 cursor-pointer rounded-control border border-field-line bg-field p-1"
              />
              {brandColor !== null ? (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setColorInput('');
                    setFormatError(null);
                  }}
                >
                  {t('color.clear')}
                </Button>
              ) : null}
            </div>

            <label className="flex items-center gap-2 text-sm text-ink">
              <input
                type="checkbox"
                checked={allowUserColorPicker}
                onChange={(event) => {
                  setAllowUserColorPicker(event.target.checked);
                }}
                className="h-4 w-4 rounded border-field-line"
              />
              {t('color.allowOverride')}
            </label>
            <p className="m-0 text-sm text-ink-muted">{t('color.contrastNote')}</p>

            {save.isError ? (
              <ErrorText message={describeError(save.error, t('color.saveError'))} />
            ) : null}
            {save.isSuccess ? (
              <p role="status" className="m-0 text-sm font-medium text-accent-text">
                {t('color.saved')}
              </p>
            ) : null}

            <Button type="submit" pending={save.isPending}>
              {t('color.save')}
            </Button>
          </form>
        )}
      </AsyncSection>
    </Panel>
  );
}

// --- Logo — whiteLabel-gated ---------------------------------------------------------------------

function LogoSection() {
  const session = useSession();

  if (!isEntitled(session.tier, 'whiteLabel')) {
    return <UpgradeRefusal feature="whiteLabel" />;
  }

  return <LogoForm />;
}

function LogoForm() {
  const { t } = useTranslation('branding');
  const session = useSession();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: orgBrandingQueryKey(session.orgId),
    queryFn: ({ signal }) => fetchOrgBranding(signal),
  });

  const [urlInput, setUrlInput] = useState('');
  // Same render-time sync as `ColorSection` above — see that field's own comment for why this is
  // not a `useEffect`.
  const [syncedFrom, setSyncedFrom] = useState<OrgBranding | undefined>(undefined);
  if (query.data !== undefined && query.data !== syncedFrom) {
    setSyncedFrom(query.data);
    setUrlInput(query.data.logoUrl ?? '');
  }

  const save = useMutation({
    mutationFn: (logoUrl: string | null) => updateOrgLogo({ logoUrl }),
    onSuccess: (branding: OrgBranding) => {
      queryClient.setQueryData(orgBrandingQueryKey(session.orgId), branding);
    },
  });

  return (
    <Panel title={t('logo.heading')}>
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          const trimmedUrl = urlInput.trim();
          save.mutate(trimmedUrl === '' ? null : trimmedUrl);
        }}
      >
        <Field
          label={t('logo.urlLabel')}
          hint={t('logo.urlHint')}
          type="url"
          placeholder="https://example.com/logo.png"
          value={urlInput}
          onChange={(event) => {
            setUrlInput(event.target.value);
          }}
        />
        {save.isError ? (
          <ErrorText message={describeError(save.error, t('logo.saveError'))} />
        ) : null}
        {save.isSuccess ? (
          <p role="status" className="m-0 text-sm font-medium text-accent-text">
            {t('logo.saved')}
          </p>
        ) : null}
        <div className="flex gap-2">
          <Button type="submit" pending={save.isPending}>
            {t('logo.save')}
          </Button>
          {query.data?.logoUrl != null ? (
            <Button
              type="button"
              variant="secondary"
              pending={save.isPending}
              onClick={() => {
                setUrlInput('');
                save.mutate(null);
              }}
            >
              {t('logo.remove')}
            </Button>
          ) : null}
        </div>
      </form>
    </Panel>
  );
}
